import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';

interface BusinessTypeDetectionModalProps {
    open: boolean;
    realTransactionCount?: number | null;
    importTransactionCount?: number;
    transactions?: Array<{ description: string; amount: number }>;
    onCancel: () => void;
    onConfirm?: () => void;
    isLoading?: boolean;
}

const BusinessTypeDetectionModal: React.FC<BusinessTypeDetectionModalProps> = ({
    open,
    realTransactionCount,
    importTransactionCount = 0,
    transactions = [],
    onCancel,
    onConfirm,
    isLoading = false
}) => {
    const { user } = useUser();
    const [businessTypes, setBusinessTypes] = useState<string[]>([]);
    const [businessTypeData, setBusinessTypeData] = useState<any[]>([]);
    const [selectedBusinessType, setSelectedBusinessType] = useState<string>('');
    const [aiSuggestedType, setAiSuggestedType] = useState<string | null>(null);
    const [isAiClassifying, setIsAiClassifying] = useState(false);
    const aiTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const hasCalledOnConfirmRef = React.useRef(false);

    const [fetchBusinessTypes, setFetchBusinessTypes] = useApi();
    const [updateBusinessType, setUpdateBusinessType] = useApi();
    const [classifyBusinessType, setClassifyBusinessType] = useApi();

    useEffect(() => {
        if (open) {
            const { url, method } = buildApiRequestConfig(routes.businessSetup.fetchTypes, [{ param: 'importCount', value: importTransactionCount }]);
            setFetchBusinessTypes({ url, method });
            
            // Call AI classification if we have enough transactions with meaningful data
            const totalTransactionCount = (realTransactionCount || 0) + (importTransactionCount || 0);
            const hasValidTransactions = transactions.some(t => t.description && t.description.trim() !== '');
            
            if (totalTransactionCount >= 100 && importTransactionCount > 0 && transactions.length > 0 && hasValidTransactions) {
                // Use actual transactions for AI classification (limit to 100)
                const limitedTransactions = transactions.slice(0, 100);
                const { url: aiUrl, method: aiMethod } = buildApiRequestConfig(routes.ai.classifyBusinessType);
                setClassifyBusinessType({
                    url: aiUrl,
                    method: aiMethod,
                    body: {
                        transactions: limitedTransactions
                    }
                });
            }
        }
    }, [open, setFetchBusinessTypes, setClassifyBusinessType, importTransactionCount, realTransactionCount]);

    useEffect(() => {
        const { data, isLoading, error } = classifyBusinessType;
        if (isLoading) {
            setIsAiClassifying(true);
        } else {
            setIsAiClassifying(false);
            if (data && data.classification && data.classification.selectedType) {
                // Set the AI suggested business type from the classification response
                setAiSuggestedType(data.classification.selectedType);
            }
        }
    }, [classifyBusinessType]);

    useEffect(() => {
        const { data, isLoading } = updateBusinessType;
        if (data && !isLoading && !hasCalledOnConfirmRef.current) {
            // Business type was successfully updated, call onConfirm callback only once
            hasCalledOnConfirmRef.current = true;
            if (onConfirm) {
                onConfirm();
            }
        }
    }, [updateBusinessType, onConfirm]);

    // Determine if button should be disabled (during any loading state)
    const isSubmitting = updateBusinessType.isLoading || isLoading;

    // Reset the ref when modal closes
    useEffect(() => {
        if (!open) {
            hasCalledOnConfirmRef.current = false;
        }
    }, [open]);

    useEffect(() => {
        const { data, isLoading, error } = fetchBusinessTypes;
        if (data && !isLoading) {
            // Extract business type names from the API response
            const typeNames = Array.isArray(data) 
                ? data.map((item: any) => item.businessType)
                : [];
            setBusinessTypes(typeNames);
            setBusinessTypeData(data || []);
            
            // Wait for AI classification to complete (with a 10 second timeout)
            if (isAiClassifying) {
                // AI is still classifying, set a timeout to fallback to default if it takes too long
                if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
                aiTimeoutRef.current = setTimeout(() => {
                    // Timeout reached, use default selection
                    setSelectedBusinessType((prevValue) => {
                        if (!prevValue && typeNames.length > 0) {
                            return typeNames.includes('Default') ? 'Default' : typeNames[0];
                        }
                        return prevValue;
                    });
                }, 10000);
            } else if (aiSuggestedType) {
                // AI classification finished with a suggestion
                if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
                if (typeNames.includes(aiSuggestedType)) {
                    setSelectedBusinessType(aiSuggestedType);
                }
            } else {
                // No AI classification or it failed, use default selection
                if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
                setSelectedBusinessType((prevValue) => {
                    if (typeNames.length > 0 && !prevValue) {
                        return typeNames.includes('Default') ? 'Default' : typeNames[0];
                    }
                    return prevValue;
                });
            }
        }
    }, [fetchBusinessTypes, aiSuggestedType, isAiClassifying]);

    // Cleanup timeout on unmount or when modal closes
    useEffect(() => {
        return () => {
            if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
        };
    }, []);

    const handleConfirm = () => {
        const selected = businessTypeData.find(item => item.businessType === selectedBusinessType);
        if (selected && user?.entityId) {
            const { url, method } = buildApiRequestConfig(routes.businessSetup.set);
            setUpdateBusinessType({
                url,
                method,
                body: { 
                    entityId: user.entityId,
                    businessDefinitionId: selected.id,
                    clearExisting: false
                }
            });
        }
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                Categories Required to Continue Import
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Primary Message */}
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        We noticed that you don't have any categories yet.
                    </Typography>

                    {/* Secondary Message with Detected Business Type or Warning */}
                    {(() => {
                        const totalTransactionCount = (realTransactionCount || 0) + (importTransactionCount || 0);
                        return totalTransactionCount < 100 ? (
                            <Box sx={{ backgroundColor: '#fff3cd', p: 2, borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
                                <Typography variant="body2" sx={{ color: '#856404' }}>
                                    We need at least 100 transactions for us to guess your business type accurately. You are only importing {importTransactionCount}. Either cancel this import and import a new file with more transactions or select one of the business types from the list shown below.
                                </Typography>
                            </Box>
                        ) : isAiClassifying ? (
                            <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, borderLeft: '4px solid #2196f3', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" sx={{ color: '#1565c0' }}>
                                    Analyzing your transactions to detect business type...
                                </Typography>
                            </Box>
                        ) : aiSuggestedType ? (
                            <Box sx={{ backgroundColor: '#d4edda', p: 2, borderRadius: 1, borderLeft: '4px solid var(--color-primary)' }}>
                                <Typography variant="body2" sx={{ color: '#155724' }}>
                                    Based on your imported transactions, we think you are a{' '}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'var(--color-primary)'
                                        }}
                                    >
                                        {aiSuggestedType}
                                    </Typography>
                                    . Feel free to change this if needed.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ backgroundColor: 'var(--color-container-background)', p: 2, borderRadius: 1, borderLeft: '4px solid var(--color-primary)' }}>
                                <Typography variant="body2" sx={{ color: '#424242' }}>
                                    Based on your imported transactions, we think you are a{' '}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'var(--color-primary)'
                                        }}
                                    >
                                        {selectedBusinessType}
                                    </Typography>
                                    .
                                </Typography>
                            </Box>
                        );
                    })()}

                    {/* Business Type Dropdown */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Business Type</InputLabel>
                        <Select
                            value={selectedBusinessType}
                            label="Business Type"
                            onChange={(e) => setSelectedBusinessType(e.target.value)}
                            disabled={isSubmitting}
                        >
                            {businessTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Supporting Text */}
                    <Typography variant="body2" sx={{ color: '#616161' }}>
                        We can automatically create a set of categories tailored to your business so you can continue importing your transactions.
                    </Typography>

                    {/* Category Count Display */}
                    {(() => {
                        const selectedData = businessTypeData.find(item => item.businessType === selectedBusinessType);
                        const categoryCount = selectedData?.categoryCount || 0;
                        return (
                            <Box sx={{ backgroundColor: 'var(--color-container-background)', p: 1.5, borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Categories to be created: <Typography component="span" sx={{ color: 'var(--color-primary)', fontWeight: 600 }}>{categoryCount}</Typography>
                                </Typography>
                            </Box>
                        );
                    })()}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    onClick={onCancel}
                    variant="outlined"
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.95rem'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        color: 'white'
                    }}
                >
                    {isSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} />
                            Creating Categories...
                        </Box>
                    ) : (
                        'Confirm'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BusinessTypeDetectionModal;
